import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, Dialog, DialogClose, IconTrash, Notice, Plate, SelectField, TextField } from "@wilco/ui";
import { useState, type SubmitEvent } from "react";
import { formatDate, t } from "../i18n.ts";
import { errorNotice, type NoticeState } from "../stock/ShelfPage.tsx";
import { LEAVE_KINDS, leaveApi, type Leave, type LeaveKind } from "./api.ts";
import { BackToHr, useActiveEmployees } from "./common.tsx";
import { holidaysQuery, leavesQuery, PEOPLE } from "./queries.ts";
import { localDate } from "./time.ts";

const route = getRouteApi("/app/hr/leave");

const STATE_TONE = { draft: "warning", approved: "success", refused: "danger", cancelled: "neutral" } as const;
type LeaveState = keyof typeof STATE_TONE;
const stateOf = (s: string): LeaveState => (s in STATE_TONE ? (s as LeaveState) : "draft");

/**
 * «Відпустки» (LEV-01, КД-08): календарные дни без праздников из списка
 * арендатора. Список праздников ведёт арендатор, по умолчанию он пуст:
 * при военном положении праздничных выходных нет.
 */
export function LeavePage() {
  const { employee } = route.useSearch();
  const navigate = useNavigate();
  const employees = useActiveEmployees();
  const current = employee ?? employees[0]?.id;
  return (
    <div className="page people stock-narrow">
      <div className="page-head">
        <BackToHr />
        <h1 className="page-title">{t("leave.title")}</h1>
      </div>
      <SelectField
        label={t("att.employee")}
        value={current ?? ""}
        onChange={(e) => void navigate({ to: "/hr/leave", search: { employee: e.target.value }, replace: true })}
        options={employees.map((e) => ({ value: e.id, label: e.name }))}
      />
      {current ? <EmployeeLeaves key={current} employeeId={current} /> : null}
      <Holidays />
    </div>
  );
}

function EmployeeLeaves({ employeeId }: { employeeId: string }) {
  const leaves = useQuery(leavesQuery(employeeId));
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const [refusing, setRefusing] = useState<Leave | null>(null);
  const today = localDate();
  const [kind, setKind] = useState<LeaveKind>("ANNUAL");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [note, setNote] = useState("");
  const done = { onSuccess: () => void queryClient.invalidateQueries({ queryKey: PEOPLE }), onError: (err: unknown) => setNotice(errorNotice(err)) };
  const request = useMutation({ mutationFn: () => leaveApi.request(employeeId, kind, from, to, note.trim()) });
  const approve = useMutation({ mutationFn: leaveApi.approve });
  const cancel = useMutation({ mutationFn: leaveApi.cancel });

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    setNotice(null);
    request.mutate(undefined, {
      ...done,
      onSuccess: (l) => {
        done.onSuccess();
        setNote("");
        setNotice({ tone: "success", title: t("leave.created", { days: l.days }) });
      },
    });
  };

  return (
    <>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      {leaves.error ? <Notice tone="danger" title={errorNotice(leaves.error).title} className="stock-notice" /> : null}
      <ul className="people-list">
        {leaves.data?.length === 0 ? <li className="w-muted people-empty">{t("leave.empty")}</li> : null}
        {leaves.data?.map((l) => {
          const state = stateOf(l.state);
          return (
            <li key={l.id} className="people-list-row people-leave">
              <span className="people-list-main">
                <span>
                  {t(`leave.kind.${l.kind}`)} · <span className="w-num">{formatDate(l.from)}–{formatDate(l.to)}</span>
                </span>
                <span className="w-muted">
                  {t("leave.days", { days: l.days })}
                  {l.note ? ` · ${l.note}` : ""}
                </span>
              </span>
              <span className="people-list-aside">
                <Badge tone={STATE_TONE[state]}>{t(`leave.state.${state}`)}</Badge>
                {state === "draft" ? (
                  <>
                    <Button variant="primary" disabled={approve.isPending} onClick={() => approve.mutate(l.id, done)}>
                      {t("leave.approve")}
                    </Button>
                    <Button variant="ghost" onClick={() => setRefusing(l)}>
                      {t("leave.refuse")}
                    </Button>
                  </>
                ) : null}
                {state === "draft" || state === "approved" ? (
                  <Button variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(l.id, done)}>
                    {t("leave.cancel")}
                  </Button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>

      <h2 className="people-h2">{t("leave.new")}</h2>
      <Plate cut="lg">
        <form className="stock-form" onSubmit={submit}>
          <SelectField label={t("leave.kind")} value={kind} onChange={(e) => setKind(e.target.value as LeaveKind)} options={LEAVE_KINDS.map((k) => ({ value: k, label: t(`leave.kind.${k}`) }))} />
          <div className="people-pair">
            <TextField label={t("leave.from")} type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
            <TextField label={t("leave.to")} type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} required />
          </div>
          <TextField label={t("leave.note")} value={note} onChange={(e) => setNote(e.target.value)} autoComplete="off" />
          <Button type="submit" variant="primary" disabled={request.isPending || !from || !to}>
            {t("leave.request")}
          </Button>
        </form>
      </Plate>
      <RefuseDialog
        open={refusing !== null}
        onClose={() => setRefusing(null)}
        onRefuse={(reason) => {
          if (refusing) leaveApi.refuse(refusing.id, reason).then(done.onSuccess, done.onError);
        }}
      />
    </>
  );
}

/** Отказ только с причиной: сотрудник её увидит. */
export function RefuseDialog({ open, onClose, onRefuse }: { open: boolean; onClose: () => void; onRefuse: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={t("leave.refuseTitle")}
      footer={
        <>
          <Button
            variant="primary"
            disabled={!reason.trim()}
            onClick={() => {
              onRefuse(reason.trim());
              setReason("");
              onClose();
            }}
          >
            {t("leave.refuse")}
          </Button>
          <DialogClose asChild>
            <Button variant="ghost">{t("common.cancel")}</Button>
          </DialogClose>
        </>
      }
    >
      <TextField label={t("leave.reason")} value={reason} onChange={(e) => setReason(e.target.value)} autoComplete="off" />
    </Dialog>
  );
}

function Holidays() {
  const holidays = useQuery(holidaysQuery);
  const queryClient = useQueryClient();
  const [day, setDay] = useState("");
  const [name, setName] = useState("");
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const done = { onSuccess: () => void queryClient.invalidateQueries({ queryKey: PEOPLE }), onError: (err: unknown) => setNotice(errorNotice(err)) };
  const add = useMutation({ mutationFn: () => leaveApi.addHoliday(day, name.trim()) });
  const remove = useMutation({ mutationFn: leaveApi.removeHoliday });
  return (
    <>
      <h2 className="people-h2">{t("leave.holidays")}</h2>
      <p className="w-muted">{t("leave.holidaysHint")}</p>
      {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
      <ul className="people-list">
        {holidays.data?.map((h) => (
          <li key={h.date} className="people-list-row">
            <span className="people-list-main">
              <span className="w-num">{formatDate(h.date)}.{h.date.slice(0, 4)}</span>
              <span className="w-muted">{h.name}</span>
            </span>
            <Button variant="ghost" size="icon" aria-label={t("leave.holidayRemove", { name: h.name })} onClick={() => remove.mutate(h.date, done)}>
              <IconTrash />
            </Button>
          </li>
        ))}
      </ul>
      <Plate cut="md">
        <form
          className="people-inline"
          onSubmit={(e) => {
            e.preventDefault();
            setNotice(null);
            add.mutate(undefined, {
              ...done,
              onSuccess: () => {
                done.onSuccess();
                setDay("");
                setName("");
              },
            });
          }}
        >
          <TextField label={t("leave.holidayDate")} type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
          <TextField label={t("leave.holidayName")} value={name} onChange={(e) => setName(e.target.value)} required autoComplete="off" />
          <Button type="submit" variant="create" disabled={add.isPending || !day || !name.trim()}>
            {t("leave.holidayAdd")}
          </Button>
        </form>
      </Plate>
    </>
  );
}
